export type CacheEntry<T> = {
  data: T;
  fetchedAt: number;
};

export type MemoryCache<T> = {
  get: (key: string, ttlMs?: number) => T | null;
  set: (key: string, data: T) => void;
  peek: (key: string) => CacheEntry<T> | undefined;
  clear: (key?: string) => void;
};

const isFresh = (entry: CacheEntry<unknown> | undefined, ttlMs?: number) => {
  if (!entry || !ttlMs) return false;
  return Date.now() - entry.fetchedAt <= ttlMs;
};

export const createMemoryCache = <T>({
  maxEntries = Infinity,
}: { maxEntries?: number } = {}): MemoryCache<T> => {
  const store = new Map<string, CacheEntry<T>>();

  return {
    get: (key: string, ttlMs?: number) => {
      const entry = store.get(key);
      if (ttlMs && !isFresh(entry, ttlMs)) return null;
      return entry?.data ?? null;
    },
    set: (key: string, data: T) => {
      store.delete(key);
      store.set(key, { data, fetchedAt: Date.now() });
      while (store.size > Math.max(1, maxEntries))
        store.delete(store.keys().next().value!);
    },
    peek: (key: string) => store.get(key),
    clear: (key?: string) => {
      if (key) {
        store.delete(key);
      } else {
        store.clear();
      }
    },
  };
};
